import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton } from '@mui/material';
import { useContext } from 'react';
import { LocaleContext } from '@zurich/common/contexts';
import { convertApiDateToDateString } from '@zurich/common/types';
import { styled } from '../../utils';
import { Avatar } from '../Avatar';
import { Typography } from '../Typography';
export function PartyData({ partyData, breakpoint, header }) {
    const { formatName } = useContext(LocaleContext);
    const placeholderPartyData = {
        title: 'Dr',
        firstName: 'Max',
        lastName: 'Mustermann',
        dayOfBirth: '1969/09/20',
        streetName: 'Musterstraße',
        houseNo: '1',
        staircase: '2',
        floorNo: '3',
        doorNo: '4',
        country: 'Österreich',
        postalCode: 2000,
        city: 'Stockerau',
        gender: 'm',
    };
    const isLoading = !partyData;
    const isPlaceholder = !partyData || !partyData?.lastName; // the BFF currently returns null values for each property of the party object, so use a placeholder instead
    const party = isPlaceholder ? placeholderPartyData : partyData;
    const birthDay = convertApiDateToDateString(party.dayOfBirth, ''); // TODO: Use this when we have finalized the api data conversion: apiDateToDate('', '', party.dayOfBirth) || '';
    const address = `${party.streetName} ${[
        party.houseNo,
        party.staircase,
        party.floorNo,
        party.doorNo,
    ]
        .filter((no) => !!no)
        .join('/')}`;
    return (_jsxs(PartyRootStyled, { breakpoint: breakpoint, children: [_jsx("div", { style: {
                    gridArea: 'avatar',
                }, children: isLoading ? (_jsx(Skeleton, { variant: "circular", children: _jsx(Avatar, { size: "m" }) })) : (_jsx(Avatar, { initials: `${party.firstName[0]}${party.lastName[0]}`, size: "m" })) }), _jsx(PartyFullNameStyled, { variant: header ? 'h5' : 'body1', component: "div", children: isLoading ? _jsx(Skeleton, {}) : _jsx("span", { children: formatName(party) }) }), _jsx(PartyBirthdayStyled, { breakpoint: breakpoint, variant: "body1", component: "div", children: isLoading ? (_jsx(Skeleton, { component: "span", style: { margin: '0 auto' }, children: _jsx("span", { children: birthDay }) })) : (birthDay) }), _jsx(PartyLocationStyled, { breakpoint: breakpoint, variant: "body1", component: "div", children: _jsx("div", { children: isLoading ? (_jsx(_Fragment, { children: _jsx(Skeleton, { component: "span", width: 200, height: 52 }) })) : (_jsxs(_Fragment, { children: [_jsx("div", { children: address }), _jsx("div", { children: `${party.postalCode} ${party.city}` })] })) }) }), _jsx(PartyCountryStyled, { breakpoint: breakpoint, variant: "body1", component: "div", children: isLoading ? (_jsx(Skeleton, { component: "span", style: { marginLeft: 'auto' }, children: _jsx("span", { children: party.country }) })) : (party.country) })] }));
}
const PartyRootStyled = styled('div', {
    shouldForwardProp: (prop) => prop !== 'breakpoint',
})(({ theme, breakpoint }) => ({
    display: 'grid',
    flex: 1,
    gridTemplateAreas: "'avatar fullName' 'avatar birthDay' 'avatar location' 'avatar country'",
    gridTemplateColumns: 'auto 1fr',
    gridColumnGap: theme.spacing(3),
    gridRowGap: theme.spacing(1),
    alignItems: 'center',
    maxWidth: '1170px',
    padding: theme.spacing(0, 2),
    ...(breakpoint && {
        [theme.breakpoints.up(breakpoint)]: {
            gridColumnGap: 'unset',
            gridRowGap: 'unset',
            gridTemplateAreas: 'unset',
            gridTemplateColumns: `[avatar] 0fr ${theme.spacing(3)} [fullName] auto [birthDay] auto [location] auto [country]`,
        },
    }),
}));
const PartyFullNameStyled = styled(Typography)(({ theme }) => ({
    gridArea: 'fullName',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.dark,
    '& span': {
        flex: 1,
        fontWeight: theme.typography.fontWeightRegular,
    },
}));
const PartyBirthdayStyled = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'breakpoint',
})(({ theme, breakpoint }) => ({
    gridArea: 'birthDay',
    ...(breakpoint && {
        [theme.breakpoints.up(breakpoint)]: {
            textAlign: 'center',
        },
    }),
}));
const PartyLocationStyled = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'breakpoint',
})(({ theme, breakpoint }) => ({
    gridArea: 'location',
    display: 'flex',
    ...(breakpoint && {
        [theme.breakpoints.up(breakpoint)]: {
            justifyContent: 'center',
        },
    }),
}));
const PartyCountryStyled = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'breakpoint',
})(({ theme, breakpoint }) => ({
    gridArea: 'country',
    ...(breakpoint && {
        [theme.breakpoints.up(breakpoint)]: {
            textAlign: 'right',
        },
    }),
}));
